import { Fragment, useState } from 'react'
import { ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { PromotionBundle } from '@/api/promotion-bundles'

interface PromotionBundleTableProps {
  bundles: PromotionBundle[]
  onEdit: (bundle: PromotionBundle) => void
  onDelete: (bundle: PromotionBundle) => void
}

function money(value: number | undefined) {
  return `¥${(value ?? 0).toFixed(2)}`
}

export function PromotionBundleTable({ bundles, onEdit, onDelete }: PromotionBundleTableProps) {
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})

  if (bundles.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
        暂无促销组合
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border bg-background">
      <Table className="min-w-[920px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>组合名称</TableHead>
            <TableHead className="w-24 text-right">商品数</TableHead>
            <TableHead className="w-28 text-right">原价</TableHead>
            <TableHead className="w-28 text-right">促销价</TableHead>
            <TableHead className="w-28 text-right">优惠</TableHead>
            <TableHead className="w-24">状态</TableHead>
            <TableHead className="w-24 text-right">排序</TableHead>
            <TableHead className="w-28 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bundles.map((bundle) => {
            const isExpanded = expanded[bundle.id]
            const items = bundle.items ?? []
            const discount = Math.max(0, (bundle.original_price ?? 0) - (bundle.promotion_price ?? 0))

            return (
              <Fragment key={bundle.id}>
                <TableRow className="hover:bg-muted/40">
                  <TableCell>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => setExpanded((prev) => ({ ...prev, [bundle.id]: !isExpanded }))}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="sr-only">{isExpanded ? '收起商品' : '展开商品'}</span>
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-0">
                      <p className="truncate font-medium">{bundle.name}</p>
                      {bundle.description && (
                        <p className="mt-1 max-w-xl truncate text-xs text-muted-foreground">{bundle.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{items.length}</TableCell>
                  <TableCell className="text-right">{money(bundle.original_price)}</TableCell>
                  <TableCell className="text-right font-medium text-red-600">{money(bundle.promotion_price)}</TableCell>
                  <TableCell className="text-right">{money(discount)}</TableCell>
                  <TableCell>
                    <Badge variant={bundle.status === 'enabled' ? 'default' : 'secondary'}>
                      {bundle.status === 'enabled' ? '启用' : '停用'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">{bundle.sort}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(bundle)}>
                        <Pencil className="h-4 w-4" />
                        <span className="sr-only">编辑</span>
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => onDelete(bundle)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">删除</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={8} className="bg-muted/20 p-3">
                      {items.length === 0 ? (
                        <div className="rounded-md border border-dashed bg-background py-6 text-center text-sm text-muted-foreground">
                          该组合暂无商品
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {items.map((item, index) => (
                            <div
                              key={item.id || `${bundle.id}-${item.sku_unique}-${index}`}
                              className="grid grid-cols-[44px_minmax(0,1fr)_100px_72px_108px] items-center gap-3 rounded-md border bg-background p-2 text-sm"
                            >
                              <img
                                src={item.product_image_snapshot}
                                alt={item.product_name_snapshot}
                                className="h-10 w-10 rounded object-cover"
                              />
                              <div className="min-w-0">
                                <p className="truncate font-medium">{item.product_name_snapshot}</p>
                                <p className="truncate text-xs text-muted-foreground">{item.sku_name_snapshot || '默认规格'}</p>
                              </div>
                              <span className="text-right">{money(item.unit_price_snapshot)}</span>
                              <span className="text-right text-muted-foreground">x{item.quantity}</span>
                              <span className="text-right font-medium">{money(item.line_original_price)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
